import * as React from 'react';
import { cn } from '@/utils/cn';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { getLifebarColor } from '@/utils/credits';
import { ACCOUNT_BILLING_CREDITS_PATH } from '@/routes/paths';

interface HeaderCreditsProps {
  isCollapsed?: boolean;
  className?: string;
}

/** Pill-shaped credit balance indicator with a tier-colored lifebar. Navigates to billing on click. */
export const HeaderCredits: React.FC<HeaderCreditsProps> = ({ isCollapsed = false, className }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const credits = user?.credits ?? 0;

  const lifebarMax = user?.lifebar_max || credits;
  const progressPercent = lifebarMax > 0 ? Math.min((credits / lifebarMax) * 100, 100) : 0;
  const lifebarColor = getLifebarColor(progressPercent);

  const handleClick = () => {
    navigate(ACCOUNT_BILLING_CREDITS_PATH);
  };

  return (
    <button
      type='button'
      onClick={handleClick}
      className={cn(
        'flex items-center transition-colors active:scale-95',
        'rounded-20 bg-brand-300/20 hover:bg-brand-300/30',
        isCollapsed ? 'h-[41px] w-full justify-center px-0' : 'h-[41px] min-w-[120px] px-4',
        !isCollapsed && 'w-[136px] text-white',
        className
      )}
    >
      {isCollapsed ? (
        <span className='font-sans text-14 font-extrabold leading-[41px] text-brand-800'>
          {credits}
        </span>
      ) : (
        <div className='flex w-full flex-col justify-center gap-[3px]'>
          <div className='h-[4px] w-full overflow-hidden rounded-[20px] bg-gray-350'>
            <div
              className={cn(
                'h-full rounded-[20px] transition-all duration-300',
                !lifebarColor && 'bg-accent-300'
              )}
              style={{
                width: `${progressPercent}%`,
                ...(lifebarColor ? { backgroundColor: lifebarColor } : {}),
              }}
            />
          </div>

          <div className='flex w-full items-end justify-between'>
            <span className='font-sans text-12 font-normal leading-[9px]'>credits</span>
            <span className='font-sans text-12 font-semibold leading-[9px]'>{credits}</span>
          </div>
        </div>
      )}
    </button>
  );
};
